import {
  Box,
  Button,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure
} from '@chakra-ui/react';
import { CheckCircleIcon } from '@chakra-ui/icons';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import Select, { StylesConfig } from 'react-select';
import { RootState } from '@/redux/store';
import {
  callToDeleteTransferFilterCollection,
  callToSaveTransferFilterCollection,
  setEccomerceStatusFilterLists,
  setIsClearAllFilterClickedOnTransfer,
  setSelectedSavedTransferFilter,
  setSkipDefaultTransferQueueFilterAutoApply,
  setSellerLicenseKeyLists,
  setSourceDescriptionLists,
  setStoreNameLists,
  setTransferFilterModel,
  setTransferFilterDeleteSuccessName,
  setTransferFilterSaveSuccess
} from '@/queue/ducks/slice';
import type { AutoCompleteApiObject } from '@/queue/ducks/slice';
import type { TransferFilterModel } from '@/queue/types';
import { TransferFilterModelInitialValue } from '@/queue/utils';
import {
  buildTransferQueueSearchFiltersPayload,
  sanitizeSearchFiltersForTransferModel,
  stripTransferQueueSearchFiltersUiMeta
} from '@/queue/transferQueueSavedFilterUiMeta';

const PRIMARY_COLOR = '#0C1E46';
const MODAL_PRIMARY_COLOR = '#0C1E46';

const selectStyles: StylesConfig<SavedFilterOption, false> = {
  container: (base) => ({
    ...base,
    maxWidth: 230,
    width: '100%'
  }),
  control: (base, state) => ({
    ...base,
    ...(state.isFocused && {
      borderColor: PRIMARY_COLOR,
      boxShadow: `0 0 0 1px ${PRIMARY_COLOR}`
    })
  }),
  menuList: (base) => ({
    ...base,
    maxHeight: 200
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor:
      state.isSelected || state.isFocused
        ? PRIMARY_COLOR
        : base.backgroundColor,
    color: state.isSelected || state.isFocused ? '#fff' : base.color,
    cursor: 'pointer'
  })
};

const NoIndicatorSeparator = () => null;

const SELECT_COMPONENTS = {
  IndicatorSeparator: NoIndicatorSeparator
} as const;

export interface SavedFilterOption {
  value: string;
  label: string;
}

function isDuplicateTransferFilterName(
  name: string,
  collections: any[],
  excludeCollectionId?: string | null
): boolean {
  const normalized = name.trim().toLowerCase();
  if (!normalized) {
    return false;
  }
  return collections.some((c: any) => {
    if (
      excludeCollectionId != null &&
      String(c?.id) === String(excludeCollectionId)
    ) {
      return false;
    }
    return (
      String(c?.name ?? '')
        .trim()
        .toLowerCase() === normalized
    );
  });
}

const DUPLICATE_FILTER_NAME_MESSAGE =
  'A filter with this name already exists. Please choose a different name.';

const MAX_SAVED_TRANSFER_FILTERS = 50;
const MAX_SAVED_FILTERS_MESSAGE =
  `You can save up to ${MAX_SAVED_TRANSFER_FILTERS} filters. Delete an existing filter to save a new one.`;

function hasAnyTransferFilterApplied(model: TransferFilterModel): boolean {
  const { createdAt, ...rest } = model;
  const restHas = Object.values(rest).some((filter: unknown) => {
    const f = filter as {
      filterType?: string;
      values?: unknown[];
      filter?: string;
    } | null;
    if (!f || typeof f !== 'object') {
      return false;
    }
    if (f.filterType === 'set' || f.filterType === 'notEqual') {
      return Array.isArray(f.values) && f.values.length > 0;
    }
    if (f.filterType === 'text') {
      return typeof f.filter === 'string' && f.filter.trim() !== '';
    }
    return false;
  });
  if (!createdAt) {
    return restHas;
  }
  const from = createdAt.dateFrom;
  const to = createdAt.dateTo;
  const hasDate =
    from != null &&
    to != null &&
    String(from).trim() !== '' &&
    String(to).trim() !== '';
  return restHas || hasDate;
}

interface AddEditTransferMultiSetFilterProps {
  placeholder?: string;
  isDisabled?: boolean;
}

type TransferFilterSuccessKind = 'save' | 'delete';

type QueueSavedFilterSlice = {
  transferFilterCollection: unknown;
  transferFilterModel: TransferFilterModel;
  sellerLicenseKeyLists: AutoCompleteApiObject[];
  isTransferFilterCollectionLoading: boolean;
  transferFilterSaveSuccessName: string | null;
  transferFilterSaveSuccessIsUpdate: boolean;
  transferFilterDeleteSuccessName: string | null;
  persistedSavedTransferFilter: { value: string; label: string } | null;
  skipDefaultTransferQueueFilterAutoApply: boolean;
};

function selectQueueSavedFilterSlice(state: RootState): QueueSavedFilterSlice {
  const q = state.queue;
  return {
    transferFilterCollection: q.transferFilterCollection,
    transferFilterModel: q.transferFilterModel,
    sellerLicenseKeyLists: q.sellerLicenseKeyLists,
    isTransferFilterCollectionLoading: q.isTransferFilterCollectionLoading,
    transferFilterSaveSuccessName: q.transferFilterSaveSuccessName,
    transferFilterSaveSuccessIsUpdate: q.transferFilterSaveSuccessIsUpdate,
    transferFilterDeleteSuccessName: q.transferFilterDeleteSuccessName,
    persistedSavedTransferFilter: q.selectedSavedTransferFilter,
    skipDefaultTransferQueueFilterAutoApply:
      q.skipDefaultTransferQueueFilterAutoApply
  };
}

type InnerProps = AddEditTransferMultiSetFilterProps & {
  queue: QueueSavedFilterSlice;
};

const AddEditTransferMultiSetFilterInner = memo(
  function AddEditTransferMultiSetFilterInner({
    placeholder = 'Select a saved filter...',
    isDisabled = false,
    queue
  }: InnerProps) {
    const [appliedFilterName, setAppliedFilterName] = useState('');
    const [isEditingName, setIsEditingName] = useState(false);
    const [filterName, setFilterName] = useState('');
    const { isOpen, onOpen, onClose } = useDisclosure();
    const {
      isOpen: isConfirmOpen,
      onOpen: onConfirmOpen,
      onClose: onConfirmClose
    } = useDisclosure();
    const {
      isOpen: isDeleteConfirmOpen,
      onOpen: onDeleteConfirmOpen,
      onClose: onDeleteConfirmClose
    } = useDisclosure();
    const {
      isOpen: isDefaultConfirmOpen,
      onOpen: onDefaultConfirmOpen,
      onClose: onDefaultConfirmClose
    } = useDisclosure();
    const {
      isOpen: isSuccessOpen,
      onOpen: onSuccessOpen,
      onClose: onSuccessClose
    } = useDisclosure();
    const [successFilterName, setSuccessFilterName] = useState('');
    const [successKind, setSuccessKind] =
      useState<TransferFilterSuccessKind | null>(null);
    const [successSaveIsUpdate, setSuccessSaveIsUpdate] = useState(false);
    const [savedFilterNameForConfirm, setSavedFilterNameForConfirm] =
      useState('');
    const [saveModalDuplicateError, setSaveModalDuplicateError] = useState('');
    const [confirmModalDuplicateError, setConfirmModalDuplicateError] =
      useState('');
    const dispatch = useDispatch();
    const transferFilterCollection: any[] =
      queue.transferFilterCollection as any[];
    const transferFilterModel = queue.transferFilterModel;
    const isTransferFilterCollectionLoading =
      queue.isTransferFilterCollectionLoading;
    const transferFilterSaveSuccessName = queue.transferFilterSaveSuccessName;
    const transferFilterSaveSuccessIsUpdate =
      queue.transferFilterSaveSuccessIsUpdate;
    const transferFilterDeleteSuccessName =
      queue.transferFilterDeleteSuccessName;
    const persistedSavedFilter = queue.persistedSavedTransferFilter;
    const skipDefaultTransferQueueFilterAutoApply =
      queue.skipDefaultTransferQueueFilterAutoApply;
    const sellerLicenseKeyLists = queue.sellerLicenseKeyLists;

    const collectionsList = useMemo(() => {
      return Array.isArray(transferFilterCollection)
        ? transferFilterCollection
        : (transferFilterCollection as { collections?: any[] })?.collections ??
            [];
    }, [transferFilterCollection]);

    const isAtMaxSavedFilters =
      collectionsList.length >= MAX_SAVED_TRANSFER_FILTERS;

    const hasActiveFilters = useMemo(
      () => hasAnyTransferFilterApplied(transferFilterModel),
      [transferFilterModel]
    );

    const filterOptions = useMemo(() => {
      if (collectionsList.length > 0) {
        return collectionsList.map((c: any) => ({
          value: String(c.id),
          label: c.name
        }));
      }
      return [];
    }, [collectionsList]);

    const selectedFilter = useMemo((): SavedFilterOption | null => {
      if (persistedSavedFilter) {
        const collection = collectionsList.find(
          (c: any) => String(c?.id) === String(persistedSavedFilter.value)
        );
        if (collection) {
          return {
            value: String(collection.id),
            label: String(collection.name ?? persistedSavedFilter.label)
          };
        }
      }
      if (skipDefaultTransferQueueFilterAutoApply) {
        return null;
      }

      const defaultCollection = collectionsList.find(
        (c: any) => c?.is_default === true
      );
      if (!defaultCollection) {
        return null;
      }

      return {
        value: String(defaultCollection.id),
        label: String(defaultCollection.name ?? '')
      };
    }, [
      persistedSavedFilter,
      collectionsList,
      skipDefaultTransferQueueFilterAutoApply
    ]);

    const selectedCollection = useMemo(
      () =>
        collectionsList.find(
          (c: any) => String(c?.id) === String(selectedFilter?.value)
        ) ?? null,
      [collectionsList, selectedFilter?.value]
    );

    const handleSavedFilterChange = useCallback(
      (option: SavedFilterOption | null) => {
        if (!option) {
          return;
        }
        dispatch(setSkipDefaultTransferQueueFilterAutoApply(false));
        dispatch(
          setSelectedSavedTransferFilter({
            value: String(option.value),
            label: option.label
          })
        );
        const collection = collectionsList.find(
          (c: any) => String(c?.id) === String(option.value)
        );
        const searchFilters = collection?.search_filters;
        if (searchFilters && typeof searchFilters === 'object') {
          const { model, sellerSelections } =
            sanitizeSearchFiltersForTransferModel(
              searchFilters as Record<string, unknown>
            );
          dispatch(setTransferFilterModel(model));
          dispatch(setSellerLicenseKeyLists(sellerSelections));
        }
      },
      [collectionsList, dispatch]
    );

    /** When Redux still has a saved selection (e.g. after route change) but the dropdown did not fire onChange, re-apply search_filters from the collection. Fingerprint avoids clobbering local filter edits on unrelated renders. */
    const persistedSavedFilterHydrateKey = useMemo(() => {
      const id = persistedSavedFilter?.value;
      if (!id || collectionsList.length === 0) {
        return '';
      }
      const collection = collectionsList.find(
        (c: any) => String(c?.id) === String(id)
      );
      if (!collection) {
        return '';
      }
      return `${String(id)}::${JSON.stringify(
        collection.search_filters ?? null
      )}`;
    }, [persistedSavedFilter?.value, collectionsList]);

    useEffect(() => {
      if (!persistedSavedFilterHydrateKey || !persistedSavedFilter?.value) {
        return;
      }
      // Preserve in-progress applied filters across dropdown close/reopen.
      // Re-hydrate from saved collection only when the model is still empty.
      if (hasAnyTransferFilterApplied(transferFilterModel)) {
        return;
      }
      const collection = collectionsList.find(
        (c: any) => String(c?.id) === String(persistedSavedFilter.value)
      );
      const searchFilters = collection?.search_filters;
      if (!searchFilters || typeof searchFilters !== 'object') {
        return;
      }
      const { model, sellerSelections } = sanitizeSearchFiltersForTransferModel(
        searchFilters as Record<string, unknown>
      );
      dispatch(setTransferFilterModel(model));
      dispatch(setSellerLicenseKeyLists(sellerSelections));
    }, [
      persistedSavedFilterHydrateKey,
      persistedSavedFilter?.value,
      transferFilterModel,
      collectionsList,
      dispatch
    ]);

    useEffect(() => {
      if (persistedSavedFilter?.value) {
        return;
      }
      if (skipDefaultTransferQueueFilterAutoApply) {
        return;
      }
      if (collectionsList.length === 0) {
        return;
      }
      const defaultCollection = collectionsList.find(
        (c: any) => c?.is_default === true
      );
      if (!defaultCollection) {
        return;
      }

      dispatch(
        setSelectedSavedTransferFilter({
          value: String(defaultCollection.id),
          label: defaultCollection.name
        })
      );
      if (
        defaultCollection.search_filters &&
        typeof defaultCollection.search_filters === 'object'
      ) {
        const { model, sellerSelections } =
          sanitizeSearchFiltersForTransferModel(
            defaultCollection.search_filters as Record<string, unknown>
          );
        dispatch(setTransferFilterModel(model));
        dispatch(setSellerLicenseKeyLists(sellerSelections));
      }
    }, [
      collectionsList,
      persistedSavedFilter?.value,
      skipDefaultTransferQueueFilterAutoApply,
      dispatch
    ]);

    useEffect(() => {
      if (selectedFilter) {
        setAppliedFilterName(selectedFilter.label);
        setIsEditingName(false);
      } else {
        setAppliedFilterName('');
        setIsEditingName(false);
      }
    }, [selectedFilter]);

    useEffect(() => {
      if (transferFilterSaveSuccessName) {
        setSuccessFilterName(transferFilterSaveSuccessName);
        setSuccessSaveIsUpdate(transferFilterSaveSuccessIsUpdate);
        setSuccessKind('save');
        setIsEditingName(false);
        onDefaultConfirmClose();
        onSuccessOpen();
        handleCloseSaveModal();
      }
    }, [
      transferFilterSaveSuccessName,
      transferFilterSaveSuccessIsUpdate
    ]);

    useEffect(() => {
      if (transferFilterDeleteSuccessName) {
        setSuccessFilterName(transferFilterDeleteSuccessName);
        setSuccessKind('delete');
        onSuccessOpen();
        onDeleteConfirmClose();
      }
    }, [transferFilterDeleteSuccessName]);

    const handleOpenSaveModal = () => {
      setFilterName('');
      setSaveModalDuplicateError('');
      onOpen();
    };

    const handleCloseSaveModal = () => {
      setFilterName('');
      setSaveModalDuplicateError('');
      onClose();
    };

    const handleSaveFilter = () => {
      if (collectionsList.length >= MAX_SAVED_TRANSFER_FILTERS) {
        setSaveModalDuplicateError(MAX_SAVED_FILTERS_MESSAGE);
        return;
      }
      const trimmed = filterName.trim();
      if (isDuplicateTransferFilterName(trimmed, collectionsList)) {
        setSaveModalDuplicateError(DUPLICATE_FILTER_NAME_MESSAGE);
        return;
      }
      setSaveModalDuplicateError('');
      dispatch(
        callToSaveTransferFilterCollection({
          name: trimmed,
          is_default: false,
          search_filters: buildTransferQueueSearchFiltersPayload(
            transferFilterModel,
            sellerLicenseKeyLists
          )
        })
      );
    };

    const handleConfirmUpdateFilter = () => {
      if (!selectedFilter) {
        return;
      }
      const newLabel = savedFilterNameForConfirm;
      if (
        isDuplicateTransferFilterName(
          newLabel,
          collectionsList,
          selectedFilter?.value ?? null
        )
      ) {
        setConfirmModalDuplicateError(DUPLICATE_FILTER_NAME_MESSAGE);
        return;
      }
      setConfirmModalDuplicateError('');
      const is_default = selectedCollection?.is_default ?? false;

      dispatch(
        callToSaveTransferFilterCollection({
          id: selectedFilter!.value,
          name: newLabel,
          search_filters: buildTransferQueueSearchFiltersPayload(
            transferFilterModel,
            sellerLicenseKeyLists
          ),
          is_default
        })
      );
      dispatch(
        setSelectedSavedTransferFilter({
          value: String(selectedFilter!.value),
          label: newLabel
        })
      );
      dispatch(setSkipDefaultTransferQueueFilterAutoApply(false));
      setAppliedFilterName(newLabel);
      setIsEditingName(false);
      onConfirmClose();
      // Success modal opens via useEffect when saga sets transferFilterSaveSuccessName
    };

    const handleConfirmSaveAsNew = () => {
      if (collectionsList.length >= MAX_SAVED_TRANSFER_FILTERS) {
        setConfirmModalDuplicateError(MAX_SAVED_FILTERS_MESSAGE);
        return;
      }
      setConfirmModalDuplicateError('');
      onConfirmClose();
      setFilterName('');
      setSaveModalDuplicateError('');
      onOpen();
    };

    const isSaveDisabled = !filterName.trim();
    const hasFilterNameChanged =
      !!selectedFilter &&
      appliedFilterName.trim() !== String(selectedFilter.label ?? '').trim();
    const hasFilterCriteriaChanged = useMemo(() => {
      if (!selectedCollection) {
        return false;
      }
      const selectedSearchFilters =
        selectedCollection?.search_filters &&
        typeof selectedCollection.search_filters === 'object'
          ? ({
              ...TransferFilterModelInitialValue,
              ...stripTransferQueueSearchFiltersUiMeta(
                selectedCollection.search_filters as Record<string, unknown>
              )
            } as TransferFilterModel)
          : (TransferFilterModelInitialValue as TransferFilterModel);
      return (
        JSON.stringify(selectedSearchFilters) !==
        JSON.stringify(transferFilterModel)
      );
    }, [selectedCollection, transferFilterModel]);
    const isSaveChangesDisabled =
      !selectedFilter ||
      !appliedFilterName.trim() ||
      (!hasFilterNameChanged && !hasFilterCriteriaChanged);

    const handleConfirmModalClose = () => {
      setConfirmModalDuplicateError('');
      onConfirmClose();
    };

    const handleSaveChanges = () => {
      if (isSaveChangesDisabled) {
        return;
      }
      setConfirmModalDuplicateError('');
      setSavedFilterNameForConfirm(appliedFilterName);
      onConfirmOpen();
    };

    const handleEditName = () => {
      setAppliedFilterName(selectedFilter?.label ?? '');
      setIsEditingName(true);
    };

    const handleDeleteFilter = () => {
      onDeleteConfirmOpen();
    };

    const handleOpenSaveAsDefaultModal = () => {
      if (!selectedFilter) {
        return;
      }
      onDefaultConfirmOpen();
    };

    const handleSaveAsDefaultFilter = () => {
      if (!selectedFilter) {
        return;
      }
      const selectedName = (appliedFilterName || selectedFilter.label).trim();
      dispatch(
        callToSaveTransferFilterCollection({
          id: selectedFilter.value,
          name: selectedName,
          search_filters: buildTransferQueueSearchFiltersPayload(
            transferFilterModel,
            sellerLicenseKeyLists
          ),
          is_default: true
        })
      );
      dispatch(
        setSelectedSavedTransferFilter({
          value: String(selectedFilter.value),
          label: selectedName
        })
      );
      dispatch(setSkipDefaultTransferQueueFilterAutoApply(false));
      setAppliedFilterName(selectedName);
      onDefaultConfirmClose();
    };

    const handleConfirmDeleteFilter = () => {
      if (!selectedFilter) {
        return;
      }
      dispatch(
        callToDeleteTransferFilterCollection({
          id: String(selectedFilter.value),
          name: selectedFilter.label
        })
      );
    };

    const clearAllFilters = () => {
      dispatch(setSkipDefaultTransferQueueFilterAutoApply(true));
      dispatch(setIsClearAllFilterClickedOnTransfer(true));
      dispatch(setSelectedSavedTransferFilter(null));
      dispatch(
        setTransferFilterModel({
          ...transferFilterModel,
          storeName: { ...transferFilterModel.storeName, values: [] },
          source: { ...transferFilterModel.source, values: [] },
          transferType: { ...transferFilterModel.transferType, values: [] },
          derivedStatus: { ...transferFilterModel.derivedStatus, values: [] },
          microStatus: { ...transferFilterModel.microStatus, values: [] },
          agent: { ...transferFilterModel.agent, values: [] },
          firearm: { ...transferFilterModel.firearm, values: [] },
          isArchive: { ...transferFilterModel.isArchive, values: [] },
          is_external: { ...transferFilterModel.is_external, values: [] },
          createdAt: {
            ...transferFilterModel.createdAt,
            dateFrom: null,
            dateTo: null
          },
          ecommerce_status: {
            ...transferFilterModel.ecommerce_status,
            values: []
          },
          sellerLicenseKey: {
            ...transferFilterModel.sellerLicenseKey,
            values: []
          }
        })
      );
      dispatch(setStoreNameLists([]));
      dispatch(setSourceDescriptionLists([]));
      dispatch(setEccomerceStatusFilterLists([]));
      dispatch(setSellerLicenseKeyLists([]));
    };

    return (
      <>
        <Box
          className="transfer-multi-set-filter-wrapper-container"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <Box className="transfer-multi-set-filter-wrapper">
            <Box className="transfer-multi-set-filter-wrapper-right">
              <Text className="transfer-save-filter-heading">
                Saved Filters:
              </Text>
              {isEditingName ? (
                <Input
                  className="saved-filter-select-wrapper"
                  value={appliedFilterName}
                  onChange={(e) => setAppliedFilterName(e.target.value)}
                  maxLength={50}
                  borderColor="gray.200"
                  _focus={{
                    borderColor: PRIMARY_COLOR,
                    boxShadow: `0 0 0 1px ${PRIMARY_COLOR}`
                  }}
                  isDisabled={isDisabled || !selectedFilter}
                  maxW="230px"
                />
              ) : (
                <Select<SavedFilterOption>
                  value={selectedFilter}
                  options={filterOptions}
                  onChange={handleSavedFilterChange}
                  placeholder={placeholder}
                  isClearable={false}
                  isDisabled={isDisabled}
                  classNamePrefix="saved-filter-select"
                  className="saved-filter-select-wrapper"
                  styles={selectStyles}
                  components={SELECT_COMPONENTS}
                />
              )}
            </Box>
            <HStack
              spacing={2}
              align="center"
              className="transfer-multi-set-filter-wrapper-left"
            >
              {(hasActiveFilters && !isEditingName)? (
                <button
                  type="button"
                  className="clear-all-btn"
                  style={{ height: '32px', padding: 'unset', color: '#666666', borderColor: '#CECECE',fontSize: '13px', fontWeight: '500', backgroundColor: '#fff' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    clearAllFilters();
                  }}
                >
                  Clear All
                </button>
              ) : null}
              {!selectedFilter ? (
                <Button
                  className="transfer-multi-set-filter-button"
                  onClick={handleOpenSaveModal}
                  isDisabled={isDisabled || !hasActiveFilters}
                  _hover={{ bg: '#0090C6' }}
                  _focus={{ outline: 'none', boxShadow: 'none' }}
                >
                <Text fontSize="20px"  color="#fff" sx={{ margin: 0,marginBottom: '1px', lineHeight: '1'}}>+</Text>
                  <Text className="transfer-multi-set-filter-button-text">
                    Save Current Filter
                  </Text>
                </Button>
              ) : null}
              {selectedFilter && !isEditingName ? (
                <>
                  {selectedCollection?.is_default === false ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleOpenSaveAsDefaultModal}
                      _focus={{ outline: 'none', boxShadow: 'none' }}
                      sx={{color: '#666666', borderColor: '#CECECE',fontSize: '13px', fontWeight: '500'}}
                      isLoading={isTransferFilterCollectionLoading}
                      loadingText=""
                      isDisabled={!selectedFilter}
                    >
                      Save as Default
                    </Button>
                  ) : null}
                  <Button
                    size="sm"
                    variant="outline"
                    className="transfer-multi-selected-filter-btn transfer-multi-selected-filter-btn-edit"
                    onClick={handleEditName}
                    bg="#0C1E46"
                    color="white"
                    _focus={{ outline: 'none', boxShadow: 'none', bg: '#0C1E46' }}
                    _hover={{ bg: '#0C1E46' }}
                    minW="32px"
                    w="32px"
                    h="32px"
                    p={0}
                    aria-label="Edit filter"
                    title="Edit"
                  >
                   <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                     <path d="M4.66114 1.76254H1.74749C1.43839 1.76254 1.14195 1.88533 0.923386 2.1039C0.70482 2.32247 0.582031 2.61891 0.582031 2.928V9.33803C0.582031 9.64713 0.70482 9.94357 0.923386 10.1621C1.14195 10.3807 1.43839 10.5035 1.74749 10.5035H8.15752C8.46662 10.5035 8.76306 10.3807 8.98162 10.1621C9.20019 9.94357 9.32298 9.64713 9.32298 9.33803V6.42438M8.499 0.938565C8.60651 0.827251 8.73511 0.738464 8.8773 0.677384C9.01949 0.616303 9.17242 0.584152 9.32717 0.582808C9.48192 0.581463 9.63539 0.610951 9.77862 0.669551C9.92185 0.728152 10.052 0.814691 10.1614 0.924119C10.2708 1.03355 10.3574 1.16367 10.416 1.3069C10.4746 1.45013 10.5041 1.6036 10.5027 1.75835C10.5014 1.9131 10.4692 2.06603 10.4081 2.20822C10.3471 2.35041 10.2583 2.47901 10.147 2.58652L5.14364 7.58984H3.49568V5.94188L8.499 0.938565Z" stroke="white" stroke-width="1.16546" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  </Button>

                  <Button
                    size="sm"
                    variant="outline"
                    borderColor="red.500"
                    color="red.500"
                    className="transfer-multi-selected-filter-btn transfer-multi-selected-filter-btn-delete"
                    onClick={handleDeleteFilter}
                    _focus={{ outline: 'none', boxShadow: 'none' }}
                    minW="32px"
                    w="32px"
                    h="32px"
                    p={0}
                    aria-label="Delete filter"
                    title="Delete"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                    >
                      <path
                        d="M11.072 4.07897L10.5668 11.1545C10.5458 11.4485 10.4143 11.7237 10.1986 11.9246C9.98284 12.1255 9.69901 12.2372 9.40423 12.2372H4.58156C4.28678 12.2372 4.00295 12.1255 3.78723 11.9246C3.57151 11.7237 3.43994 11.4485 3.41901 11.1545L2.91378 4.07897M5.82743 6.40989V9.90626M8.15835 6.40989V9.90626M8.74108 4.07897V2.33078C8.74108 2.17623 8.67969 2.02801 8.5704 1.91872C8.46112 1.80944 8.3129 1.74805 8.15835 1.74805H5.82743C5.67288 1.74805 5.52466 1.80944 5.41538 1.91872C5.3061 2.02801 5.2447 2.17623 5.2447 2.33078V4.07897M2.33105 4.07897H11.6547"
                        stroke="currentColor"
                        strokeWidth="1.16546"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Button>
                </>
              ) : null}
              {isEditingName ? (
                <Button
                  size="sm"
                  bg={PRIMARY_COLOR}
                  color="white"
                  _hover={{ opacity: 0.9 }}
                  _focus={{ outline: 'none', boxShadow: 'none' }}
                  sx={{ fontSize: '13px', fontWeight: '500' }}
                  onClick={handleSaveChanges}
                  isLoading={isTransferFilterCollectionLoading}
                  loadingText=""
                  isDisabled={isSaveChangesDisabled}
                >
                  Save Changes
                </Button>
              ) : null}
            </HStack>
          </Box>
        </Box>
        <Modal isOpen={isOpen} onClose={handleCloseSaveModal} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader fontWeight={600} color={MODAL_PRIMARY_COLOR}>
              Save Filter
            </ModalHeader>
            <ModalBody>
              <Input
                placeholder="Enter filter name"
                value={filterName}
                onChange={(e) => {
                  setFilterName(e.target.value);
                  setSaveModalDuplicateError('');
                }}
                maxLength={50}
                isDisabled={isAtMaxSavedFilters}
                borderColor={saveModalDuplicateError ? 'red.500' : 'gray.200'}
                _focus={{
                  borderColor: MODAL_PRIMARY_COLOR,
                  boxShadow: `0 0 0 1px ${MODAL_PRIMARY_COLOR}`
                }}
              />
              {isAtMaxSavedFilters ? (
                <Text color="red.500" fontSize="sm" mt={2}>
                  {MAX_SAVED_FILTERS_MESSAGE}
                </Text>
              ) : saveModalDuplicateError ? (
                <Text color="red.500" fontSize="sm" mt={2}>
                  {saveModalDuplicateError}
                </Text>
              ) : null}
            </ModalBody>
            <ModalFooter gap={2}>
              <Button
                variant="ghost"
                onClick={handleCloseSaveModal}
                _focus={{ outline: 'none', boxShadow: 'none' }}
              >
                Cancel
              </Button>
              <Button
                bg={MODAL_PRIMARY_COLOR}
                color="white"
                _hover={{ bg: MODAL_PRIMARY_COLOR }}
                _focus={{ outline: 'none', boxShadow: 'none' }}
                onClick={handleSaveFilter}
                isDisabled={isSaveDisabled || isAtMaxSavedFilters}
                isLoading={isTransferFilterCollectionLoading}
                loadingText=""
              >
                Save
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
        <Modal
          isOpen={isConfirmOpen}
          onClose={handleConfirmModalClose}
          isCentered
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader fontWeight={600} color={MODAL_PRIMARY_COLOR}>
              Save Changes or save as a new filter
            </ModalHeader>
            <ModalBody>
              <Text color={MODAL_PRIMARY_COLOR}>
                Are you sure you want to update this saved filter with your
                changes? You can also save your changes as a new filter
              </Text>
              {isAtMaxSavedFilters ? (
                <Text color="red.500" fontSize="sm" mt={3}>
                  {MAX_SAVED_FILTERS_MESSAGE}
                </Text>
              ) : null}
              {confirmModalDuplicateError ? (
                <Text color="red.500" fontSize="sm" mt={3}>
                  {confirmModalDuplicateError}
                </Text>
              ) : null}
            </ModalBody>
            <ModalFooter gap={2}>
              <Button
                variant="outline"
                borderColor="gray.300"
                color={MODAL_PRIMARY_COLOR}
                _hover={{ bg: 'gray.50' }}
                _focus={{ outline: 'none', boxShadow: 'none' }}
                onClick={handleConfirmSaveAsNew}
                isDisabled={isAtMaxSavedFilters}
                title={
                  isAtMaxSavedFilters ? MAX_SAVED_FILTERS_MESSAGE : undefined
                }
                isLoading={isTransferFilterCollectionLoading}
                loadingText=""
              >
                Save as New
              </Button>
              <Button
                bg={MODAL_PRIMARY_COLOR}
                color="white"
                _hover={{ opacity: 0.9 }}
                _focus={{ outline: 'none', boxShadow: 'none' }}
                onClick={handleConfirmUpdateFilter}
                isLoading={isTransferFilterCollectionLoading}
                loadingText=""
                isDisabled={!selectedFilter}
              >
                Save
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
        <Modal
          isOpen={isDefaultConfirmOpen}
          onClose={onDefaultConfirmClose}
          isCentered
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader fontWeight={700} color={MODAL_PRIMARY_COLOR}>
              Save {selectedFilter?.label ? `${selectedFilter.label} ` : ''}as
              default filter
            </ModalHeader>
            <ModalBody>
              <Text color="#666666">
              This filter will be automatically applied each time you reopen this page. Any previously set default filter will be removed.
              </Text>
            </ModalBody>
            <ModalFooter gap={2}>
              <Button
                variant="ghost"
                color="#666666"
                _hover={{ bg: 'transparent' }}
                _focus={{ outline: 'none', boxShadow: 'none' }}
                onClick={onDefaultConfirmClose}
              >
                Cancel
              </Button>
              <Button
                bg={MODAL_PRIMARY_COLOR}
                color="white"
                _hover={{ bg: MODAL_PRIMARY_COLOR }}
                _focus={{ outline: 'none', boxShadow: 'none' }}
                onClick={handleSaveAsDefaultFilter}
                isLoading={isTransferFilterCollectionLoading}
                loadingText=""
                isDisabled={!selectedFilter}
              >
                Confirm
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
        <Modal
          isOpen={isDeleteConfirmOpen}
          onClose={onDeleteConfirmClose}
          isCentered
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader fontWeight={600} color={MODAL_PRIMARY_COLOR}>
              Delete Filter
            </ModalHeader>
            <ModalBody>
              <Text color={MODAL_PRIMARY_COLOR}>
                Are you sure you want to delete &quot;
                <Text as="span" fontWeight={600}>
                  {selectedFilter?.label}
                </Text>
                &quot;? This action cannot be undone.
              </Text>
            </ModalBody>
            <ModalFooter gap={2}>
              <Button
                variant="outline"
                borderColor="gray.300"
                color={MODAL_PRIMARY_COLOR}
                _hover={{ bg: 'gray.50' }}
                _focus={{ outline: 'none', boxShadow: 'none' }}
                onClick={onDeleteConfirmClose}
              >
                Cancel
              </Button>
              <Button
                bg="red.500"
                color="white"
                _hover={{ bg: 'red.600' }}
                _focus={{ outline: 'none', boxShadow: 'none' }}
                onClick={handleConfirmDeleteFilter}
                isLoading={isTransferFilterCollectionLoading}
                loadingText=""
              >
                Delete
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
        <Modal isOpen={isSuccessOpen} onClose={onSuccessClose} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalBody pt={6} pb={6} pl={6} pr={6}>
              <HStack align="flex-start" spacing={4} mb={4}>
                <Box
                  flexShrink={0}
                  w={10}
                  h={10}
                  borderRadius="full"
                  bg="green.100"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <CheckCircleIcon w={6} h={6} color="green.500" aria-hidden />
                </Box>
                <Box>
                  <Text
                    fontWeight={600}
                    fontSize="lg"
                    color={MODAL_PRIMARY_COLOR}
                    mb={1}
                  >
                    {successKind === 'save' && successSaveIsUpdate
                      ? 'Updated!'
                      : 'Success!'}
                  </Text>
                  <Text color={MODAL_PRIMARY_COLOR} fontSize="sm">
                    {successKind === 'delete' ? (
                      <>
                        Filter{' '}
                        <Text as="span" fontWeight="bold">
                          &quot;{successFilterName}&quot;
                        </Text>{' '}
                        has been deleted successfully!
                      </>
                    ) : successSaveIsUpdate ? (
                      <>
                        Filter{' '}
                        <Text as="span" fontWeight="bold">
                          &quot;{successFilterName}&quot;
                        </Text>{' '}
                        has been updated successfully!
                      </>
                    ) : (
                      <>
                        Filter{' '}
                        <Text as="span" fontWeight="bold">
                          &quot;{successFilterName}&quot;
                        </Text>{' '}
                        has been saved successfully!
                      </>
                    )}
                  </Text>
                </Box>
              </HStack>
              <Box textAlign="right">
                <Button
                  bg={MODAL_PRIMARY_COLOR}
                  color="white"
                  _hover={{ opacity: 0.9 }}
                  _focus={{ outline: 'none', boxShadow: 'none' }}
                  onClick={() => {
                    if (successKind === 'delete') {
                      dispatch(setTransferFilterDeleteSuccessName(null));
                    } else {
                      dispatch(setTransferFilterSaveSuccess(null));
                    }
                    setSuccessKind(null);
                    setSuccessSaveIsUpdate(false);
                    onSuccessClose();
                  }}
                >
                  OK
                </Button>
              </Box>
            </ModalBody>
          </ModalContent>
        </Modal>
      </>
    );
  }
);

function AddEditTransferMultiSetFilter(
  props: AddEditTransferMultiSetFilterProps
) {
  const queue = useSelector(selectQueueSavedFilterSlice, shallowEqual);
  return <AddEditTransferMultiSetFilterInner {...props} queue={queue} />;
}

export default AddEditTransferMultiSetFilter;
